import type { ClientTheme } from "@/app/lib/themes"

interface TogglePillGroupProps {
  /** Selected values. Empty array = "All" (no constraint). */
  selected: string[]
  onChange: (next: string[]) => void
  options: string[]
  theme: ClientTheme
  /** Fixed min-width per pill — set for uniform grids (e.g. section numbers). */
  pillMinWidth?: string
}

/**
 * Multi-select as a wrapping group of toggle pills. For small fixed sets
 * (interest statuses) and numeric grids (sections), seeing every option at
 * once beats opening a dropdown.
 */
export function TogglePillGroup({
  selected,
  onChange,
  options,
  theme,
  pillMinWidth,
}: TogglePillGroupProps) {
  const toggle = (opt: string) =>
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt])

  if (options.length === 0) {
    return (
      <span style={{ fontFamily: "var(--font-ui)", fontSize: "12px", color: theme.textDim }}>
        No options
      </span>
    )
  }

  return (
    <div className="flex flex-wrap" style={{ gap: "6px" }}>
      {options.map((opt) => {
        const active = selected.includes(opt)
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            aria-pressed={active}
            className="rounded-full"
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "12px",
              fontWeight: active ? 600 : 500,
              padding: "4px 10px",
              minWidth: pillMinWidth,
              textAlign: "center",
              color: active ? theme.brandPrimary : theme.textMuted,
              backgroundColor: active ? `${theme.brandPrimary}14` : "transparent",
              border: `1px solid ${active ? `${theme.brandPrimary}50` : theme.borderColor}`,
              cursor: "pointer",
              transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
            }}
          >
            {opt}
          </button>
        )
      })}
    </div>
  )
}
